import { api } from './api';

// Query parameters
export interface DateRangeParams {
  fromDate: string;
  toDate: string;
}

export interface TimelineParams extends DateRangeParams {
  groupBy?: 'day' | 'week' | 'month';
}

export interface TopNParams extends DateRangeParams {
  limit?: number;
}

export interface GeographicParams extends DateRangeParams {
  wilayaId?: number;
}

export interface ForecastParams {
  months?: number;
  atcCode?: string;
}

export interface ExportParams extends DateRangeParams {
  format: 'csv' | 'pdf';
  reportType: string;
}

// Response types
export interface TimelineDataPoint {
  date: string;
  count: number;
}

export interface TopDrugStat {
  drugId: string;
  tradeName: string;
  dci: string;
  atcCode: string | null;
  totalQuantity: number;
  prescriptionCount: number;
}

export interface StatusBreakdown {
  status: string;
  count: number;
  percentage: number;
}

export interface InteractionStat {
  severity: string;
  count: number;
  drugs: { drug1: string; drug2: string }[];
}

export interface DoctorOverview {
  totalPrescriptions: number;
  fulfilledPrescriptions: number;
  partiallyFulfilledPrescriptions: number;
  expiredPrescriptions: number;
  cancelledPrescriptions: number;
  activePrescriptions: number;
}

export interface PharmacistOverview {
  totalFulfillments: number;
  totalItemsDispensed: number;
  partialFulfillments: number;
  completeFulfillments: number;
  uniquePatients: number;
  uniquePrescribers: number;
}

export interface SubstitutionRate {
  totalItems: number;
  substitutedItems: number;
  substitutionRate: number;
  topSubstitutions: {
    originalDrug: string;
    substitutedDrug: string;
    count: number;
  }[];
}

export interface PartialFulfillmentRate {
  totalFulfillments: number;
  partialFulfillments: number;
  completeFulfillments: number;
  partialRate: number;
}

export interface DrugTrendByATC {
  atcCode: string;
  atcName: string;
  timeline: TimelineDataPoint[];
}

export interface ControlledSubstanceStat {
  controlledClass: string;
  drugCount: number;
  totalPrescribed: number;
  totalDispensed: number;
}

export interface GeographicStat {
  wilayaId: number;
  wilayaName: string;
  prescriptionCount: number;
  fulfillmentCount: number;
  fulfillmentRate: number;
}

export interface FulfillmentRateStat {
  period: string;
  totalPrescriptions: number;
  fulfilledPrescriptions: number;
  fulfillmentRate: number;
  averageTimeToFulfillmentHours: number;
}

export interface SeasonalTrend {
  month: number;
  monthName: string;
  averagePrescriptions: number;
  topDrugs: string[];
}

export interface DrugForecast {
  drugId: string;
  tradeName: string;
  dci: string;
  atcCode: string | null;
  forecastedQuantity: number;
  confidence: number;
  trend: 'increasing' | 'stable' | 'decreasing';
}

// Service methods
export const reportsService = {
  // Doctor reports
  async getDoctorOverview(params: DateRangeParams): Promise<DoctorOverview> {
    const response = await api.get('/reports/doctor/overview', { params });
    return response.data;
  },

  async getDoctorTimeline(params: TimelineParams): Promise<TimelineDataPoint[]> {
    const response = await api.get('/reports/doctor/timeline', { params });
    return response.data.timeline;
  },

  async getDoctorTopDrugs(params: TopNParams): Promise<TopDrugStat[]> {
    const response = await api.get('/reports/doctor/top-drugs', { params });
    return response.data.drugs;
  },

  async getDoctorStatusBreakdown(params: DateRangeParams): Promise<StatusBreakdown[]> {
    const response = await api.get('/reports/doctor/status-breakdown', { params });
    return response.data.breakdown;
  },

  async getDoctorInteractions(params: DateRangeParams): Promise<InteractionStat[]> {
    const response = await api.get('/reports/doctor/interactions', { params });
    return response.data.interactions;
  },

  // Pharmacist reports
  async getPharmacistOverview(params: DateRangeParams): Promise<PharmacistOverview> {
    const response = await api.get('/reports/pharmacist/overview', { params });
    return response.data;
  },

  async getPharmacistTimeline(params: TimelineParams): Promise<TimelineDataPoint[]> {
    const response = await api.get('/reports/pharmacist/timeline', { params });
    return response.data.timeline;
  },

  async getPharmacistTopDrugs(params: TopNParams): Promise<TopDrugStat[]> {
    const response = await api.get('/reports/pharmacist/top-drugs', { params });
    return response.data.drugs;
  },

  async getPharmacistSubstitutionRate(params: DateRangeParams): Promise<SubstitutionRate> {
    const response = await api.get('/reports/pharmacist/substitution-rate', { params });
    return response.data;
  },

  async getPharmacistPartialRate(params: DateRangeParams): Promise<PartialFulfillmentRate> {
    const response = await api.get('/reports/pharmacist/partial-rate', { params });
    return response.data;
  },

  // Admin stats
  async getAdminStats(): Promise<{
    activeUsers: number;
    prescriptionsThisMonth: number;
    fulfillmentsThisMonth: number;
    totalDrugs: number;
  }> {
    const response = await api.get('/reports/admin/stats');
    return response.data;
  },

  // Government reports
  async getGovDrugTrends(params: TimelineParams & { atcPrefix?: string }): Promise<DrugTrendByATC[]> {
    const response = await api.get('/reports/government/drug-trends', { params });
    return response.data.trends;
  },

  async getGovTopDrugsByATC(params: TopNParams): Promise<{ atcClass: string; drugs: TopDrugStat[] }[]> {
    const response = await api.get('/reports/government/top-drugs-atc', { params });
    return response.data.atcGroups;
  },

  async getGovControlledSubstances(params: DateRangeParams): Promise<ControlledSubstanceStat[]> {
    const response = await api.get('/reports/government/controlled', { params });
    return response.data.substances;
  },

  async getGovGeographic(params: GeographicParams): Promise<GeographicStat[]> {
    const response = await api.get('/reports/government/geographic', { params });
    return response.data.geographic;
  },

  async getGovFulfillmentRate(params: TimelineParams): Promise<FulfillmentRateStat[]> {
    const response = await api.get('/reports/government/fulfillment-rate', { params });
    return response.data.fulfillmentRates;
  },

  async getGovSeasonalTrends(params: DateRangeParams): Promise<SeasonalTrend[]> {
    const response = await api.get('/reports/government/seasonal', { params });
    return response.data.seasonal;
  },

  async getGovForecast(params: ForecastParams): Promise<DrugForecast[]> {
    const response = await api.get('/reports/government/forecast', { params });
    return response.data.forecast;
  },

  async exportReport(params: ExportParams): Promise<Blob> {
    const response = await api.get('/reports/government/export', {
      params,
      responseType: 'blob',
    });
    return response.data;
  },
};
